import { Experience } from "../../../../context/dataExperienceContext";
import { BookingQuestion } from "../../../../types/bookingQuestions";

type TravelerPickup = Experience['product']['logistics']['travelerPickup'];

const arrivalAirQuestions = [
    'TRANSFER_AIR_ARRIVAL_AIRLINE',
    'TRANSFER_AIR_ARRIVAL_FLIGHT_NO',
    'TRANSFER_ARRIVAL_TIME',
    'TRANSFER_ARRIVAL_DROP_OFF',
    'PICKUP_POINT'
];

const arrivalRailQuestions = [
    'TRANSFER_RAIL_ARRIVAL_LINE',
    'TRANSFER_RAIL_ARRIVAL_STATION',
    'TRANSFER_ARRIVAL_TIME',
    'TRANSFER_ARRIVAL_DROP_OFF'
];

const arrivalSeaQuestions = [
    'TRANSFER_PORT_ARRIVAL_TIME',
    'TRANSFER_PORT_CRUISE_SHIP',
    'TRANSFER_ARRIVAL_DROP_OFF',
    'PICKUP_POINT'
];

const arrivalOtherQuestions = [
    'PICKUP_POINT'
];

const departureAirQuestions = [
    'TRANSFER_AIR_DEPARTURE_AIRLINE',
    'TRANSFER_AIR_DEPARTURE_FLIGHT_NO',
    'TRANSFER_DEPARTURE_DATE',
    'TRANSFER_DEPARTURE_TIME',
    'TRANSFER_DEPARTURE_PICKUP'
];

const departureRailQuestions = [
    'TRANSFER_RAIL_DEPARTURE_LINE',
    'TRANSFER_RAIL_DEPARTURE_STATION',
    'TRANSFER_DEPARTURE_DATE',
    'TRANSFER_DEPARTURE_TIME',
    'TRANSFER_DEPARTURE_PICKUP'
];

const departureSeaQuestions = [
    'TRANSFER_PORT_CRUISE_SHIP',
    'TRANSFER_DEPARTURE_DATE',
    'TRANSFER_PORT_DEPARTURE_TIME',
    'TRANSFER_DEPARTURE_PICKUP'
];

export const isConditionallyRequired = (questionId: string, arrivalMode: string, departureMode: string, travelerPickup: TravelerPickup, questions: BookingQuestion[]): boolean => {
    // If the question is in any of the mode-specific arrays and its corresponding mode question is not present,
    // it should be considered mandatory
    const hasArrivalMode = questions.some(q => q.id === 'TRANSFER_ARRIVAL_MODE');
    const hasDepartureMode = questions.some(q => q.id === 'TRANSFER_DEPARTURE_MODE');

    const isInArrivalAir = arrivalAirQuestions.includes(questionId);
    const isInArrivalRail = arrivalRailQuestions.includes(questionId);
    const isInArrivalSea = arrivalSeaQuestions.includes(questionId);
    const isInArrivalOther = arrivalOtherQuestions.includes(questionId);

    const isInDepartureAir = departureAirQuestions.includes(questionId);
    const isInDepartureRail = departureRailQuestions.includes(questionId);
    const isInDepartureSea = departureSeaQuestions.includes(questionId);

    // If it's an arrival question and TRANSFER_ARRIVAL_MODE is not present, make it mandatory
    if (!hasArrivalMode && (isInArrivalAir || isInArrivalRail || isInArrivalSea || isInArrivalOther)) {
        return true;
    }

    // If it's a departure question and TRANSFER_DEPARTURE_MODE is not present, make it mandatory
    if (!hasDepartureMode && (isInDepartureAir || isInDepartureRail || isInDepartureSea)) {
        return true;
    }

    const arrivalRequiredQuestions = getRequiredQuestionsForMode(arrivalMode, true, travelerPickup);
    const departureRequiredQuestions = getRequiredQuestionsForMode(departureMode, false, travelerPickup);

    return arrivalRequiredQuestions.includes(questionId) || departureRequiredQuestions.includes(questionId);
};

const getRequiredQuestionsForMode = (mode: string, isArrival: boolean, travelerPickup: TravelerPickup): string[] => {
    const allowCustomPickup = travelerPickup?.allowCustomTravelerPickup;

    if (isArrival) {
        switch (mode) {
            case 'AIR':
                const hasAirportLocations = travelerPickup?.locations?.some(loc => loc.pickupType === 'AIRPORT');

                if (!hasAirportLocations && !allowCustomPickup) {
                    // Remove TRANSFER_ARRIVAL_DROP_OFF and PICKUP_POINT if no airport locations and custom not allowed
                    return arrivalAirQuestions.filter(question =>
                        question !== 'TRANSFER_ARRIVAL_DROP_OFF' && question !== 'PICKUP_POINT'
                    );
                }
                return arrivalAirQuestions;
            case 'RAIL':
                return arrivalRailQuestions;
            case 'SEA':
                const hasPortLocations = travelerPickup?.locations?.some(loc => loc.pickupType === 'PORT');

                if (!hasPortLocations && !allowCustomPickup) {
                    // Remove location-related questions if no port locations and custom not allowed
                    return arrivalSeaQuestions.filter(question =>
                        question !== 'TRANSFER_ARRIVAL_DROP_OFF' && question !== 'PICKUP_POINT'
                    );
                }
                return arrivalSeaQuestions;
            case 'OTHER':
                const hasOtherLocations = travelerPickup?.locations?.some(loc =>
                    ['HOTEL', 'LOCATION', 'OTHER'].includes(loc.pickupType)
                );

                if (!hasOtherLocations && !allowCustomPickup) {
                    // Remove location-related questions if no other locations and custom not allowed
                    return arrivalOtherQuestions.filter(question =>
                        question !== 'TRANSFER_ARRIVAL_DROP_OFF' && question !== 'PICKUP_POINT'
                    );
                }
                return arrivalOtherQuestions;
            default:
                return [];
        }
    } else {
        switch (mode) {
            case 'AIR':
                return departureAirQuestions;
            case 'RAIL':
                return departureRailQuestions;
            case 'SEA':
                return departureSeaQuestions;
            case 'OTHER':
                return [];
            default:
                return [];
        }
    }
};

export const shouldSkipQuestionBasedOnArrivalMode = (questionId: string, arrivalMode: string, departureMode: string): boolean => {
    if (![
        ...arrivalAirQuestions,
        ...arrivalRailQuestions,
        ...arrivalSeaQuestions,
        ...arrivalOtherQuestions,
        ...departureAirQuestions,
        ...departureRailQuestions,
        ...departureSeaQuestions,
    ].includes(questionId)) {
        return false
    }

    const isInArrivalAir = arrivalAirQuestions.includes(questionId);
    const isInArrivalRail = arrivalRailQuestions.includes(questionId);
    const isInArrivalSea = arrivalSeaQuestions.includes(questionId);
    const isInArrivalOther = arrivalOtherQuestions.includes(questionId);

    const isInDepartureAir = departureAirQuestions.includes(questionId);
    const isInDepartureRail = departureRailQuestions.includes(questionId);
    const isInDepartureSea = departureSeaQuestions.includes(questionId);


    // Check if the question belongs to arrival questions
    if (arrivalMode === 'AIR' && isInArrivalAir) return false;
    if (arrivalMode === 'RAIL' && isInArrivalRail) return false;
    if (arrivalMode === 'SEA' && isInArrivalSea) return false;
    if (arrivalMode === 'OTHER' && isInArrivalOther) return false;


    if (departureMode === 'AIR' && isInDepartureAir) return false;
    if (departureMode === 'RAIL' && isInDepartureRail) return false;
    if (departureMode === 'SEA' && isInDepartureSea) return false;

    // If the question doesn't belong to either set, don't skip it
    return true;
};

export const getAvailableTransferModes = (questionId: string, questions: BookingQuestion[]) => {
    const modes = ["OTHER"];

    if (questionId === "TRANSFER_ARRIVAL_MODE") {
        if (questions.some(q => [
            "TRANSFER_AIR_ARRIVAL_AIRLINE",
            "TRANSFER_AIR_ARRIVAL_FLIGHT_NO",
            'TRANSFER_ARRIVAL_TIME',
            'TRANSFER_ARRIVAL_DROP_OFF'
        ].includes(q.id))) {
            modes.push("AIR");
        }
        if (questions.some(q => [
            "TRANSFER_PORT_ARRIVAL_TIME",
            "TRANSFER_PORT_CRUISE_SHIP",
            "TRANSFER_ARRIVAL_DROP_OFF",
        ].includes(q.id))) {
            modes.push("SEA");
        }
        if (questions.some(q => [
            "TRANSFER_RAIL_ARRIVAL_LINE",
            "TRANSFER_RAIL_ARRIVAL_STATION",
            'TRANSFER_ARRIVAL_TIME',
            'TRANSFER_ARRIVAL_DROP_OFF'
        ].includes(q.id))) {
            modes.push("RAIL");
        }
    } else if (questionId === "TRANSFER_DEPARTURE_MODE") {
        if (questions.some(q => [
            'TRANSFER_AIR_DEPARTURE_AIRLINE',
            'TRANSFER_AIR_DEPARTURE_FLIGHT_NO',
            'TRANSFER_DEPARTURE_DATE',
            'TRANSFER_DEPARTURE_TIME',
            'TRANSFER_DEPARTURE_PICKUP'].includes(q.id))) {
            modes.push("AIR");
        }
        if (questions.some(q => [
            'TRANSFER_PORT_CRUISE_SHIP',
            'TRANSFER_DEPARTURE_DATE',
            'TRANSFER_PORT_DEPARTURE_TIME',
            'TRANSFER_DEPARTURE_PICKUP'].includes(q.id))) {
            modes.push("SEA");
        }
        if (questions.some(q => [
            'TRANSFER_RAIL_DEPARTURE_LINE',
            'TRANSFER_RAIL_DEPARTURE_STATION',
            'TRANSFER_DEPARTURE_DATE',
            'TRANSFER_DEPARTURE_TIME',
            'TRANSFER_DEPARTURE_PICKUP'].includes(q.id))) {
            modes.push("RAIL");
        }
    }
    return modes;
};


export const getFilteredPickupLocations = (selectedArrivalMode: string, question: BookingQuestion, travelerPickup: TravelerPickup) => {
    if (!travelerPickup?.locations) return [];

    const locations = travelerPickup.locations;

    switch (selectedArrivalMode) {
        case 'AIR':
            return locations.filter(loc => loc.pickupType === 'AIRPORT');
        case 'SEA':
            return locations.filter(loc => loc.pickupType === 'PORT');
        case 'OTHER':
            return locations.filter(loc =>
                ['HOTEL', 'LOCATION', 'OTHER'].includes(loc.pickupType)
            );
        default:
            return [];
    }
};